import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/cn";
import { Surface } from "./Surface";

/**
 * A lightweight anchored popover: a trigger plus a floating paper panel that
 * closes on outside-pointer / Escape. The panel is portaled to `document.body`
 * and `position: fixed`, measured from the trigger via `side` / `align` — so it
 * always floats above app chrome and never gets clipped by a scrolling ancestor
 * (the sticky composer, the sidebar list). Compose richer menus on top of this.
 */
export interface PopoverProps {
  /** Renders the trigger; wire `toggle`/`open` into your button. */
  trigger: (args: { open: boolean; toggle: () => void; close: () => void }) => ReactNode;
  /** Panel content; receives `close` so items can dismiss after acting. */
  children: (args: { close: () => void }) => ReactNode;
  side?: "top" | "bottom";
  align?: "start" | "end";
  /** Accessible label for the panel region. */
  label: string;
  /** Extra classes for the panel (e.g. width). */
  panelClassName?: string;
  className?: string;
}

interface PanelPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  maxHeight: number;
}

export function Popover({
  trigger,
  children,
  side = "bottom",
  align = "start",
  label,
  panelClassName,
  className,
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PanelPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const close = () => setOpen(false);
  const toggle = () => setOpen((value) => !value);

  // Anchor the fixed panel to the trigger and cap it to the space on its opening
  // side so it never spills past the viewport. Recomputed on resize/scroll
  // because the trigger (docked composer) can move.
  useLayoutEffect(() => {
    if (!open) return;
    const GAP = 8; // matches the old mt-2 / mb-2
    const EDGE = 12; // breathing room from the viewport edge
    const MIN_ROOM = 240; // below this, prefer the roomier side
    const measure = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const spaceBelow = vh - rect.bottom - GAP - EDGE;
      const spaceAbove = rect.top - GAP - EDGE;
      // Open on the requested side, but flip when it's too cramped and the other
      // side is roomier — so a control low in a scrolling panel opens upward
      // instead of squashing into a few scrolling rows.
      const openTop =
        side === "top"
          ? !(spaceAbove < MIN_ROOM && spaceBelow > spaceAbove)
          : spaceBelow < MIN_ROOM && spaceAbove > spaceBelow;
      const next: PanelPosition = {
        maxHeight: Math.max(160, openTop ? spaceAbove : spaceBelow),
      };
      if (openTop) next.bottom = vh - rect.top + GAP;
      else next.top = rect.bottom + GAP;
      if (align === "start") next.left = rect.left;
      else next.right = vw - rect.right;
      setPos(next);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, side, align]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative inline-flex", className)}>
      {trigger({ open, toggle, close })}
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              style={{ position: "fixed", top: pos.top, bottom: pos.bottom, left: pos.left, right: pos.right }}
              className="z-50"
            >
              <Surface
                id={panelId}
                role="dialog"
                aria-label={label}
                tone="panel-2"
                radius="md"
                border="none"
                shadow="pill"
                style={{ maxHeight: pos.maxHeight }}
                className={cn("flex min-h-0 min-w-[200px] flex-col overflow-hidden p-1", panelClassName)}
              >
                {children({ close })}
              </Surface>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
